import { motion } from 'framer-motion';
import type { MotionStyle } from 'framer-motion';
import type { HTMLAttributes, ReactNode } from 'react';

const cx = (...names: (string | false | undefined)[]): string => names.filter(Boolean).join(' ');

// framer-motion redefines these handlers with animation-specific signatures,
// which conflict with the standard DOM event handler types from HTMLAttributes.
type MotionConflictingHandlers =
  | 'onAnimationStart'
  | 'onAnimationEnd'
  | 'onAnimationIteration'
  | 'onDrag'
  | 'onDragStart'
  | 'onDragEnd';

export interface CardProps extends Omit<HTMLAttributes<HTMLDivElement>, MotionConflictingHandlers> {
  /** Add default internal padding (skip when composing header/body). */
  padded?: boolean;
  /** Hover elevation + pointer cursor for clickable cards. */
  interactive?: boolean;
}

export function Card({
  padded = false,
  interactive = false,
  className,
  children,
  style,
  ...rest
}: CardProps): JSX.Element {
  const motionProps = interactive
    ? {
        whileHover: { y: -4, scale: 1.01 },
        transition: { type: 'spring' as const, stiffness: 400, damping: 25 },
      }
    : {};

  return (
    <motion.div
      className={cx(
        'bg-white dark:bg-zinc-900/50 border border-zinc-200 dark:border-white/5 shadow-soft dark:shadow-glass-dark rounded-3xl overflow-hidden transition-all duration-300',
        padded && 'p-5',
        interactive && 'cursor-pointer hover:border-brand-500/30 hover:shadow-glow-brand',
        className,
      )}
      {...(style !== undefined ? { style: style as MotionStyle } : {})}
      {...rest}
      {...motionProps}
    >
      {children}
    </motion.div>
  );
}

export function CardHeader({
  title,
  subtitle,
  action,
  icon,
}: {
  title: ReactNode;
  subtitle?: ReactNode;
  action?: ReactNode;
  icon?: ReactNode;
}): JSX.Element {
  return (
    <div className="flex items-center justify-between p-5 pb-2">
      <div className="flex items-center gap-3">
        {icon && <div className="text-zinc-400 dark:text-zinc-500">{icon}</div>}
        <div className="flex flex-col">
          <span className="font-semibold text-sm text-zinc-900 dark:text-zinc-50 tracking-tight">
            {title}
          </span>
          {subtitle !== undefined && (
            <span className="text-xs text-zinc-500 dark:text-zinc-400">{subtitle}</span>
          )}
        </div>
      </div>
      {action && <div>{action}</div>}
    </div>
  );
}

export function CardBody({
  children,
  className,
  ...rest
}: HTMLAttributes<HTMLDivElement>): JSX.Element {
  return (
    <div className={cx('p-5 pt-3', className)} {...rest}>
      {children}
    </div>
  );
}
